import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, ConfirmDialog, DataGrid, StatusBadge, useToast, type DataGridColumn } from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Company } from '@kss/api';
import { companyService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

export default function AllCompanies() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const perms = useModulePerms('Company');
  const [rows, setRows] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await companyService.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.companyId) return;
    setDeleting(true);
    try { await companyService.delete(confirmDelete.companyId); toast.success('Deleted', `${confirmDelete.name} removed`); setConfirmDelete(null); await load(); }
    catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<Company>[] = [
    // The live Company row has no `code` field — the legacy column
    // always rendered blank. Show `legalName` instead so the table
    // gives at least one identifying secondary label per row.
    {
      accessor: 'legalName',
      title: 'Legal name',
      sortable: true,
      filterable: true,
      render: (r) => (r as Company & { legalName?: string }).legalName || '—',
    },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'gstNumber', title: 'GST', sortable: true, filterable: true, render: (r) => r.gstNumber || '—' },
    {
      // Live column is `panNumber`; legacy `pan` accessor never
      // resolved.
      accessor: 'panNumber',
      title: 'PAN',
      sortable: true,
      filterable: true,
      render: (r) => (r as Company & { panNumber?: string }).panNumber || '—',
    },
    { accessor: 'isActive', title: 'Status', sortable: true, render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'companyId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); if (r.companyId) navigate(`/panel/companies/${encodeId(r.companyId)}`); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Company>
        title={<span className="hidden sm:inline">All companies</span>}
        columnPicker data={rows} columns={columns} defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <StandardAddDropdown basePath="/panel/companies" newLabel="New company" service={companyService} templateFields={['name', 'legalName', 'gstNumber', 'panNumber', 'email', 'phone', 'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country', 'logoUrl', 'isActive']} module="Company" />
          </div>
        }
      />
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))} onConfirm={handleDelete}
        title="Delete company" message={confirmDelete ? `Remove "${confirmDelete.name}"?` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}
